/**
 * ScrollToTop — universal "back to top" floating pill that fades in when
 * the user scrolls below the fold. Apr 29 2026 follow-up #4 — Lead's
 * call: "Add a Scroll-to-Top button. Place at the bottom of 'A Note From
 * Midnite' section. Should smoothly scroll back to the top when clicked.
 * And for every page."
 *
 * Mounted once at the App root so it works on /, /demo, /world, /profile,
 * /roots, /about, /contact — every page. Positioned bottom-RIGHT-OF-CENTER
 * (above the install pill which lives in the bottom-right corner) so it
 * doesn't collide. `prefers-reduced-motion` honors are respected — we
 * fall back to instant scroll for users who've opted out of animations.
 */
import { useEffect, useState, useCallback } from 'react';
import { ArrowUp } from 'lucide-react';

export default function ScrollToTop({ threshold = 320 }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setVisible((window.scrollY || document.documentElement.scrollTop || 0) > threshold);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);

  const handleClick = useCallback(() => {
    const reduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, left: 0, behavior: reduced ? 'auto' : 'smooth' });
  }, []);

  if (!visible) return null;

  return (
    <button
      onClick={handleClick}
      data-testid="scroll-to-top"
      aria-label="Scroll to top"
      title="Back to top"
      className="fixed z-[9999] bottom-3 right-1/2 translate-x-1/2 md:bottom-16 md:right-3 md:translate-x-0 inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#D4A84A]/40 bg-[#0A0A0A]/85 backdrop-blur-sm text-[#D4A84A] shadow-xl hover:bg-[#D4A84A]/12 hover:border-[#D4A84A] transition-all"
    >
      <ArrowUp size={16} />
    </button>
  );
}
